import { action, computed, makeObservable, observable, override, toJS } from 'mobx';
import ElectionsModule from 'store/modules/elections/electionsModule';
import { Subscriber } from 'types/electionService';
import { SubscriberPresenter } from 'store/modules/elections/types';
import IFullFormPageStore from './iFullFormPageStore';
import { BaseFullEmployeeFormDomainParams, ElectionsTabId, TabIdWithElections } from './types';
import { createElectionsCardPresenter } from './electionsCardPresenter';

/**
 * Full-form page store that owns an ElectionsModule for elections.
 * Primary + dependents SSOT remains {@link IFullFormPageStore.subscriberBasicInfo}.
 */
export default abstract class IFullFormPageStoreWithElections<
  DomainParams extends BaseFullEmployeeFormDomainParams,
> extends IFullFormPageStore<DomainParams, TabIdWithElections> {
  protected _electionsModule: ElectionsModule;

  public get electionsModule(): ElectionsModule {
    return this._electionsModule;
  }

  /** SubscriberCard model: election rows + live basic-info primary/dependents. */
  public get electionsCardPresenter(): SubscriberPresenter {
    const election = this._electionsModule.subscribers[0]?.electionPresenter;
    const basicInfo = this._subscriberBasicInfo?.basicInfo;
    if (!election || !basicInfo) {
      throw new Error('Cannot create elections card presenter without elections and basic-info');
    }
    return createElectionsCardPresenter(toJS(election), toJS(basicInfo));
  }

  public get isElectionsTabValid(): boolean {
    if (!this._electionsModule.subscribers[0] || !this._subscriberBasicInfo) {
      return true;
    }
    return this._electionsModule.isSubscriberValid(this.electionsCardPresenter);
  }

  protected async loadSubscriber(subscriberId: string): Promise<Pick<Subscriber, 'id' | 'common' | 'custom'> | null> {
    return this._electionsModule.fetchSubscriberById(subscriberId);
  }

  protected getDependentFormId(): string | undefined {
    return this._electionsModule.requiredSubInfo?.manageDependentFormId;
  }

  protected static override isValidTabId(value: string): boolean {
    return super.isValidTabId(value) || (Object.values(ElectionsTabId) as string[]).includes(value);
  }

  private _dependentIdsFromBasicInfo(): string[] {
    return (this._subscriberBasicInfo?.basicInfo.dependents ?? []).map((dependent) => dependent.id).filter(Boolean);
  }

  public override setCurrentTabId(tabId: string): void {
    super.setCurrentTabId(tabId);
    if (this._currentTabId === ElectionsTabId.ELECTIONS) {
      this._electionsModule.subscribers[0]?.syncElectionDefaults(this._dependentIdsFromBasicInfo());
    }
  }

  public override async onAddAnotherEmployee(): Promise<void> {
    this._electionsModule.resetSubscribers();
    await super.onAddAnotherEmployee();
  }

  /** Opens the product-line rate form and writes answers through basic-info SSOT. */
  public async openProductLineRateForm(formId: string, productId = ''): Promise<void> {
    const basicInfo = this._subscriberBasicInfo;
    const election = this._electionsModule.subscribers[0]?.electionPresenter;
    if (!basicInfo || !election) {
      throw new Error('Cannot open product line rate form without elections and basic-info');
    }

    await this._electionsModule.openProductLineRateForm(
      formId,
      election.employeeClass,
      toJS(basicInfo.primary),
      productId,
      async (answers) => {
        await basicInfo.updatePrimary(answers);
        await this.onProductLineRateAnswersSaved();
      },
    );
  }

  /** Enrollment refreshes rates after save; quote does not display them. */
  // eslint-disable-next-line class-methods-use-this
  protected onProductLineRateAnswersSaved(): Promise<void> {
    return Promise.resolve();
  }

  constructor(domainData: DomainParams, electionsModule: ElectionsModule) {
    super(domainData);
    this._electionsModule = electionsModule;

    makeObservable<IFullFormPageStoreWithElections<DomainParams>, '_electionsModule'>(this, {
      _electionsModule: observable,
      electionsModule: computed,
      electionsCardPresenter: computed,
      isElectionsTabValid: computed,
      setCurrentTabId: override,
      onAddAnotherEmployee: override,
      openProductLineRateForm: action.bound,
    });
  }
}
